import * as React from "react";
import { Container, Card, CardContent, CardHeader, Typography, List, ListItem, ListItemIcon, ListItemText, Divider, Box, Chip, Button } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {ReactNode} from "react";


const plans = [
    {
        id: "guest",
        title: "Guest",
        subtitle: "Free access",
        highlight: false,
        cta: "Continue as Guest",
        features: {
            "Browse public content": true,
            "Create account": true,
            "Save preferences": false,
            "Comment & interact": false,
            "Priority support": false,
            "Advanced analytics": false,
        },
    },
    {
        id: "registered",
        title: "Registered",
        subtitle: "Standard user",
        highlight: true,
        cta: "Create Account",
        features: {
            "Browse public content": true,
            "Create account": true,
            "Save preferences": true,
            "Comment & interact": true,
            "Priority support": false,
            "Advanced analytics": false,
        },
    },
    {
        id: "premium",
        title: "Premium User",
        subtitle: "Full access",
        highlight: false,
        cta: "Upgrade to Premium",
        features: {
            "Browse public content": true,
            "Create account": true,
            "Save preferences": true,
            "Comment & interact": true,
            "Priority support": true,
            "Advanced analytics": true,
        },
    },
];

function FeatureItem({ enabled, label }:{enabled:boolean, label:ReactNode}) {
    return (
        <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 32 }}>
                {enabled ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                    <CancelIcon color="disabled" fontSize="small" />
                )}
            </ListItemIcon>
            <ListItemText
                primary={label}
                slotProps={{
                    primary: { variant: "body2" }
                }}
            />
        </ListItem>
    );
}

export function FeatureComparisonPage() {
    const featureKeys = Object.keys(plans[0].features);

    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <Box textAlign="center" mb={5}>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                    Compare Plans
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Choose the access level that fits your needs
                </Typography>
            </Box>

            <Box sx={{display: "flex", justifyContent: "center", flexDirection: {xs: "column", md: "row"}, gap: "30px"}}>
                {plans.map((plan) => (
                        <Card
                            variant="outlined"
                            sx={{
                                height: "100%",
                                borderWidth: plan.highlight ? 2 : 1,
                                borderColor: plan.highlight ? "primary.main" : "divider",
                                position: "relative",
                            }}
                        >
                            {plan.highlight && (
                                <Chip
                                    label="Most Popular"
                                    color="primary"
                                    size="small"
                                    sx={{ position: "absolute", top: 12, right: 12 }}
                                />
                            )}

                            <CardHeader
                                title={plan.title}
                                subheader={plan.subtitle}
                                slotProps={{
                                    title: { align: "center", fontWeight: 700 },
                                    subheader: { align: "center" }
                                }}
                            />

                            <CardContent>
                                <Divider sx={{ mb: 2 }} />

                                <List dense>
                                    {featureKeys.map((key) => (
                                        <FeatureItem
                                            key={key}
                                            label={key}
                                            enabled={plan.features[key as keyof typeof plan.features]}
                                        />
                                    ))}
                                </List>

                                <Box mt={3} textAlign="center">
                                    <Button
                                        variant={plan.highlight ? "contained" : "outlined"}
                                        fullWidth
                                    >
                                        {plan.cta}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                ))}
            </Box>
        </Container>
    );
}
